import './types';
import './ui/responsive.css';
import './ui/debug/debug.css';
import { IndexedDBStorage } from './storage/IndexedDBStorage';
import { SaveSlotManager } from './storage/SaveSlotManager';
import { ModelLoader } from './model/ModelLoader';
import { UserModelStore } from './model/UserModelStore';
import { WorkerBridge } from './emulator/worker-bridge/WorkerBridge';
import { InlineWorker, isWorkerAvailable } from './emulator/worker-bridge/InlineWorker';
import { UISettingsManager } from './ui/UISettings';
import { SkeuomorphicUI } from './ui/SkeuomorphicUI';
import { MinimalUI } from './ui/MinimalUI';
import { InputManager } from './input/InputManager';
import { MouseInput } from './input/MouseInput';
import { TouchInput } from './input/TouchInput';
import { KeyboardInput } from './input/KeyboardInput';
import { ModelSelector } from './ui/ModelSelector';
import { SaveSlotPanel } from './ui/SaveSlotPanel';
import { ToolBar } from './ui/ToolBar';
import { StatusBar } from './ui/StatusBar';
import { DebugPanel } from './ui/debug/DebugPanel';
import { DisassemblerView } from './ui/debug/DisassemblerView';
import { RegisterView } from './ui/debug/RegisterView';
import { MemoryView } from './ui/debug/MemoryView';
import { BreakpointManager } from './ui/debug/BreakpointManager';
import { InteractiveTerminal } from './ui/debug/InteractiveTerminal';
import { validateModelConfig } from './model/ModelConfig';
import type { ModelConfig } from './model/ModelConfig';
import type { KeyMappingEntry } from './input/KeyboardInput';
import type { EmulatorState } from './emulator/worker-bridge/messages';
import type { InputEvent } from './input/InputProvider';

class App {
  private storage: IndexedDBStorage;
  private saveSlotManager!: SaveSlotManager;
  private userModelStore!: UserModelStore;
  private modelLoader!: ModelLoader;
  private bridge!: WorkerBridge;
  private uiSettings: UISettingsManager;
  private inputManager!: InputManager;
  private currentUI: SkeuomorphicUI | MinimalUI | null = null;
  private currentModelConfig: ModelConfig | null = null;
  private isRunning = false;
  private currentScale: number;
  private toolbar!: ToolBar;
  private modelSelector!: ModelSelector;
  private saveSlotPanel!: SaveSlotPanel;
  private canvasContainer!: HTMLDivElement;
  private appEl!: HTMLElement;
  private statusBar!: StatusBar;
  private debugPanel: DebugPanel | null = null;
  private disassembler: DisassemblerView | null = null;
  private registerView: RegisterView | null = null;
  private memoryView: MemoryView | null = null;
  private breakpointManager: BreakpointManager | null = null;
  private terminal: InteractiveTerminal | null = null;
  private debugModeEnabled: boolean = false;

  constructor() {
    this.storage = new IndexedDBStorage();
    this.uiSettings = new UISettingsManager();
    const settings = this.uiSettings.getSettings();
    this.currentScale = settings.scale;
  }

  async init(): Promise<void> {
    await this.storage.init();

    this.saveSlotManager = new SaveSlotManager(this.storage);
    this.userModelStore = new UserModelStore(this.storage);
    this.modelLoader = new ModelLoader({ storage: this.storage, userModelStore: this.userModelStore });

    this.appEl = document.getElementById('app')!;

    if (!isWorkerAvailable()) {
      this.bridge = new WorkerBridge(new InlineWorker());
    } else {
      const worker = new Worker(
        new URL('./worker.ts', import.meta.url),
        { type: 'module' },
      );
      this.bridge = new WorkerBridge(worker);
    }
    this.bridge.setStorageProvider(this.storage);

    this.buildLayout();

    this.inputManager = new InputManager((event: InputEvent) => {
      this.bridge.sendInputEvent(event).catch(() => {});
    });

    this.bridge.onStateUpdate = (state: EmulatorState) => {
      this.onEmulatorStateUpdate(state);
    };

    this.bridge.onError = (error: Error) => {
      this.showError('Emulator error: ' + error.message);
    };

    await this.modelSelector.refresh();

    try {
      await this.loadBuiltinModel('fx-991cn-x-verc');
    } catch (err) {
      this.showError('Failed to load default model: ' + (err as Error).message);
    }
  }

  private buildLayout(): void {
    this.appEl.innerHTML = '';
    this.appEl.style.flexDirection = 'column';

    const header = document.createElement('div');
    header.className = 'app-header';
    const title = document.createElement('h2');
    title.textContent = 'CasioEmuWeb';
    header.appendChild(title);
    this.appEl.appendChild(header);

    const mainLayout = document.createElement('div');
    mainLayout.className = 'main-layout';
    this.appEl.appendChild(mainLayout);

    const sidebar = document.createElement('div');
    sidebar.className = 'sidebar';
    mainLayout.appendChild(sidebar);

    this.modelSelector = new ModelSelector(
      sidebar,
      this.modelLoader,
      this.userModelStore,
      {
        onSelectBuiltin: (id: string) => this.loadBuiltinModel(id),
        onSelectUserModel: (id: string) => this.loadUserModel(id),
        onUploadUserModel: (files: FileList) => this.handleUserModelUpload(files),
        onDeleteUserModel: (id: string) => this.handleUserModelDelete(id),
      },
    );

    this.saveSlotPanel = new SaveSlotPanel(
      sidebar,
      {
        onSave: (name: string) => this.handleSaveSlot(name),
        onLoad: (id: string) => this.handleLoadSlot(id),
        onDelete: (id: string) => this.handleDeleteSlot(id),
      },
    );

    const mainArea = document.createElement('div');
    mainArea.className = 'main-area';
    mainLayout.appendChild(mainArea);

    const settings = this.uiSettings.getSettings();

    this.toolbar = new ToolBar(
      mainArea,
      this.currentScale,
      settings.mode,
      {
        onSpeedChange: (speed: number) => this.handleSpeedChange(speed),
        onScaleIncrease: () => this.changeScale('increase'),
        onScaleDecrease: () => this.changeScale('decrease'),
        onModeToggle: () => this.toggleMode(),
        onSaveSlot: () => this.handleSaveSlotUI(),
        onStartPause: () => this.toggleRunning(),
        onStep: () => this.stepOnce(),
        onReset: () => this.resetEmulator(),
        onDebugToggle: () => this.toggleDebugMode(),
      },
    );

    this.canvasContainer = document.createElement('div');
    this.canvasContainer.className = 'canvas-container';
    mainArea.appendChild(this.canvasContainer);

    const errorBar = document.createElement('div');
    errorBar.className = 'error-bar';
    errorBar.style.display = 'none';
    mainArea.appendChild(errorBar);

    this.statusBar = new StatusBar(this.appEl);
    this.statusBar.updateMode(this.bridge.getMode());

    this.refreshSaveSlots();
  }

  private handleSpeedChange(speed: number): void {
    this.statusBar.updateSpeed(speed);
    this.bridge.setSpeed(speed).catch(() => {});
  }

  private assembleDebugPanel(): void {
    if (this.debugPanel) return;

    this.debugPanel = new DebugPanel(this.bridge);
    this.disassembler = new DisassemblerView(this.bridge);
    this.registerView = new RegisterView(this.bridge);
    this.memoryView = new MemoryView(this.bridge);
    this.breakpointManager = new BreakpointManager(this.bridge);
    this.terminal = new InteractiveTerminal(this.bridge);

    this.debugPanel.addPanel('Disassembler', this.disassembler.el);
    this.debugPanel.addPanel('Registers', this.registerView.el);
    this.debugPanel.addPanel('Memory', this.memoryView.el);
    this.debugPanel.addPanel('Breakpoints', this.breakpointManager.el);
    this.debugPanel.addPanel('Terminal', this.terminal.el);

    this.appEl.appendChild(this.debugPanel.el);
    this.debugPanel.hide();
  }

  private toggleDebugMode(): void {
    if (!this.debugModeEnabled) {
      this.assembleDebugPanel();

      this.bridge.onBreakpointHit = (bp: unknown) => {
        this.onBreakpointHit(bp);
      };

      this.debugModeEnabled = true;
      this.debugPanel!.show();
      this.toolbar.updateDebugState(true);

      if (this.registerView) {
        this.registerView.refresh().catch(() => {});
      }
      if (this.disassembler) {
        this.disassembler.goToAddress(0, 0).catch(() => {});
      }
      if (this.memoryView) {
        this.memoryView.goToAddress(0).catch(() => {});
      }
    } else {
      this.debugModeEnabled = false;
      this.bridge.onBreakpointHit = null;
      this.debugPanel!.hide();
      this.toolbar.updateDebugState(false);
    }
  }

  private onBreakpointHit(bp: unknown): void {
    if (!this.debugModeEnabled) return;

    const data = bp as Record<string, unknown>;
    const address = typeof data.address === 'number' ? data.address : 0;
    const csr = typeof data.csr === 'number' ? data.csr : 0;

    if (this.isRunning) {
      this.isRunning = false;
      this.currentUI?.stop();
      this.toolbar.updateRunning(false);
      this.statusBar.updateRunning(false);
    }

    if (this.registerView) {
      this.registerView.refresh().catch(() => {});
    }
    if (this.disassembler) {
      this.disassembler.goToAddress(address, csr).catch(() => {});
      this.disassembler.updatePC(address, csr);
    }
  }

  private async refreshDebugViews(): Promise<void> {
    if (!this.debugModeEnabled) return;
    if (this.registerView) {
      this.registerView.refresh().catch(() => {});
    }
    if (this.disassembler) {
      this.disassembler.followPC().catch(() => {});
    }
  }

  private async refreshSaveSlots(): Promise<void> {
    try {
      const slots = await this.saveSlotManager.listSlots();
      this.saveSlotPanel.showSlots(slots);
    } catch {
    }
  }

  private async showError(msg: string): Promise<void> {
    const bar = this.appEl.querySelector('.error-bar') as HTMLElement | null;
    if (!bar) return;
    bar.textContent = msg;
    bar.style.display = 'block';
    setTimeout(() => {
      bar.style.display = 'none';
    }, 5000);
  }

  private async loadBuiltinModel(modelId: string): Promise<void> {
    try {
      const model = await this.modelLoader.loadBuiltinModel(modelId);
      await this.switchModel(model);
    } catch (err) {
      this.showError('Failed to load built-in model: ' + (err as Error).message);
    }
  }

  private async loadUserModel(modelId: string): Promise<void> {
    try {
      const model = await this.modelLoader.loadStoredUserModel(modelId);
      await this.switchModel(model);
    } catch (err) {
      this.showError('Failed to load user model: ' + (err as Error).message);
    }
  }

  private async handleUserModelUpload(files: FileList): Promise<void> {
    try {
      const fileArray = Array.from(files);
      const model = await this.modelLoader.loadUserModel(fileArray);
      validateModelConfig(model);

      let imageData: Uint8Array | undefined;
      if (model.interfaceImage) {
        const canvas = new OffscreenCanvas(model.interfaceImage.width, model.interfaceImage.height);
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.putImageData(model.interfaceImage, 0, 0);
          const blob = await canvas.convertToBlob({ type: 'image/png' });
          imageData = new Uint8Array(await blob.arrayBuffer());
        }
      }

      await this.userModelStore.saveUserModel(model, model.romData!, imageData);
      await this.modelSelector.refresh();
      await this.switchModel(model);
    } catch (err) {
      this.showError('Upload failed: ' + (err as Error).message);
    }
  }

  private async handleUserModelDelete(modelId: string): Promise<void> {
    try {
      await this.userModelStore.deleteStoredUserModel(modelId);
      await this.modelSelector.refresh();
    } catch (err) {
      this.showError('Failed to delete model: ' + (err as Error).message);
    }
  }

  private async switchModel(model: ModelConfig): Promise<void> {
    const wasRunning = this.isRunning;

    if (this.isRunning) {
      await this.bridge.pause();
      this.isRunning = false;
    }

    if (this.currentUI) {
      this.currentUI.stop();
      this.currentUI.destroy();
      this.currentUI = null;
    }

    this.inputManager.detachAll();

    try {
      await this.bridge.init(model);
    } catch (err) {
      this.showError('Failed to initialize model: ' + (err as Error).message);
      return;
    }

    this.currentModelConfig = model;

    const settings = this.uiSettings.getSettings();
    this.createUIForMode(settings.mode, model);

    // CasioEmuNeo: IMG_Load → interface_texture → Frame() 渲染
    if (model.interfaceImage && this.currentUI) {
      const bitmap = await createImageBitmap(model.interfaceImage);
      if ('setInterfaceImage' in this.currentUI) {
        (this.currentUI as SkeuomorphicUI).setInterfaceImage(bitmap);
      }
    }

    this.setupInputProviders(model);

    if (wasRunning) {
      await this.bridge.start();
      this.isRunning = true;
    }

    this.toolbar.updateRunning(this.isRunning);
    this.statusBar.updateRunning(this.isRunning);
  }

  private createUIForMode(mode: 'skeuomorphic' | 'minimal', model: ModelConfig): SkeuomorphicUI | MinimalUI {
    let ui: SkeuomorphicUI | MinimalUI;
    if (mode === 'minimal') {
      ui = new MinimalUI(this.canvasContainer);
      (ui as MinimalUI).setScreenConfig({ cols: model.screen.displayCols, rows: model.screen.rows });
      const keyMappedButtons = model.buttons.map(b => ({
        keyCode: b.keyCode,
        label: b.label || b.keyCode,
      }));
      (ui as MinimalUI).setButtons(keyMappedButtons);
      (ui as MinimalUI).setScale(this.currentScale);
      (ui as MinimalUI).onInput = (event) => {
        this.bridge.sendInputEvent(event).catch(() => {});
      };
      (ui as MinimalUI).setFrameBufferSource(() => this.getFrameBufferSource());
      this.inputManager.attachAll((ui as MinimalUI).canvas);
    } else {
      ui = new SkeuomorphicUI(this.canvasContainer);
      (ui as SkeuomorphicUI).setScreenConfig({ cols: model.screen.displayCols, rows: model.screen.rows });
      (ui as SkeuomorphicUI).setButtons(model.buttons.map(b => ({
        id: b.keyCode,
        x: b.x,
        y: b.y,
        w: b.w,
        h: b.h,
      })));
      (ui as SkeuomorphicUI).setScale(this.currentScale);
      (ui as SkeuomorphicUI).setFrameBufferSource(() => this.getFrameBufferSource());
      (ui as SkeuomorphicUI).bindInputManager(this.inputManager);
    }
    this.currentUI = ui;
    return ui;
  }

  private getFrameBufferSource(): { frameBuffer: Uint8Array; statusIcons: Record<string, boolean> } | null {
    if (!this.currentModelConfig) return null;
    if (!this.bridge || !this.currentModelConfig) return null;
    const state = this.bridge.getLatestState();
    if (!state) return null;
    return {
      frameBuffer: state.frameBuffer,
      statusIcons: state.statusIcons,
    };
  }

  private setupInputProviders(model: ModelConfig): void {
    this.inputManager.detachAll();
    this.inputManager.addProvider(new MouseInput(model.buttons, this.currentScale));
    this.inputManager.addProvider(new TouchInput(model.buttons, this.currentScale));

    const keyboardMapping = new Map<string, KeyMappingEntry>();
    for (const entry of model.keyMap) {
      let physical = entry.keyCode;
      if (physical.startsWith('K_')) {
        physical = physical.slice(2).toLowerCase();
      }
      keyboardMapping.set(physical, { keyCode: entry.keyCode, scanCode: entry.scanCode });
      keyboardMapping.set(entry.keyCode, { keyCode: entry.keyCode, scanCode: entry.scanCode });
    }
    this.inputManager.addProvider(new KeyboardInput(keyboardMapping));
  }

  private onEmulatorStateUpdate(state: EmulatorState): void {
    if (this.currentUI) {
      const pressed = new Set(state.pressedButtons);
      this.currentUI.setPressedButtons(pressed);
    }
    this.statusBar.updateTicks(state.totalTicks);
    this.statusBar.updateRunning(state.isRunning);
  }

  private async toggleRunning(): Promise<void> {
    try {
      if (this.isRunning) {
        await this.bridge.pause();
        this.isRunning = false;
        this.currentUI?.stop();
        this.statusBar.updateRunning(false);
        await this.refreshDebugViews();
      } else {
        await this.bridge.start();
        this.isRunning = true;
        this.currentUI?.start();
        this.statusBar.updateRunning(true);
      }
      this.toolbar.updateRunning(this.isRunning);
    } catch (err) {
      this.showError('Control error: ' + (err as Error).message);
    }
  }

  private async stepOnce(): Promise<void> {
    try {
      if (this.isRunning) return;
      await this.bridge.step();
      await this.refreshDebugViews();
    } catch (err) {
      this.showError('Step error: ' + (err as Error).message);
    }
  }

  private async resetEmulator(): Promise<void> {
    try {
      if (!confirm('Reset the emulator? The current state will be lost.')) return;
      await this.bridge.reset();
    } catch (err) {
      this.showError('Reset error: ' + (err as Error).message);
    }
  }

  private async changeScale(direction: 'increase' | 'decrease'): Promise<void> {
    const step = 0.25;
    const min = 0.5;
    const max = 4;
    let newScale = direction === 'increase' ? this.currentScale + step : this.currentScale - step;
    newScale = Math.max(min, Math.min(max, newScale));
    newScale = Math.round(newScale * 100) / 100;
    this.currentScale = newScale;
    this.uiSettings.updateSettings({ scale: newScale });
    this.toolbar.updateScale(newScale);
    this.currentUI?.setScale(newScale);

    if (this.currentModelConfig && this.currentUI) {
      this.inputManager.detachAll();
      this.setupInputProviders(this.currentModelConfig);
      this.inputManager.attachAll((this.currentUI as SkeuomorphicUI | MinimalUI).canvas);
    }
  }

  private async toggleMode(): Promise<void> {
    const settings = this.uiSettings.getSettings();
    const newMode = settings.mode === 'skeuomorphic' ? 'minimal' : 'skeuomorphic';
    this.uiSettings.updateSettings({ mode: newMode });
    this.toolbar.updateMode(newMode);

    if (!this.currentModelConfig || !this.currentUI) return;

    const wasStarted = this.isRunning;
    this.currentUI.stop();
    this.currentUI.destroy();
    this.currentUI = null;

    const model = this.currentModelConfig;
    const newUI = this.createUIForMode(newMode, model);

    if (wasStarted) {
      newUI.start();
    }
  }

  private async handleSaveSlotUI(): Promise<void> {
    const nameInput = this.saveSlotPanel.el.querySelector('.slot-name-input') as HTMLInputElement;
    const name = nameInput?.value.trim() || ('Save-' + new Date().toLocaleString());
    await this.handleSaveSlot(name);
    if (nameInput) nameInput.value = '';
  }

  private async handleSaveSlot(name: string): Promise<void> {
    try {
      if (!this.currentModelConfig || !this.currentUI) return;
      const snapshot = await this.bridge.saveSnapshotSerialized();
      const screenshot = this.currentUI.renderer.captureScreenshot();

      const modelId = this.currentModelConfig.modelName.replace(/[^a-zA-Z0-9_-]/g, '_');

      await this.saveSlotManager.saveSlot(
        modelId + '-' + Date.now(),
        name,
        snapshot,
        modelId,
        screenshot.length > 0 ? screenshot : undefined,
      );
      await this.refreshSaveSlots();
    } catch (err) {
      this.showError('Save failed: ' + (err as Error).message);
    }
  }

  private async handleLoadSlot(slotId: string): Promise<void> {
    try {
      const snapshot = await this.saveSlotManager.loadSlot(slotId);
      if (!snapshot) {
        this.showError('Failed to load save slot: not found');
        return;
      }
      await this.bridge.loadSnapshotSerialized(snapshot);
    } catch (err) {
      this.showError('Load failed: ' + (err as Error).message);
    }
  }

  private async handleDeleteSlot(slotId: string): Promise<void> {
    try {
      await this.saveSlotManager.deleteSlot(slotId);
      await this.refreshSaveSlots();
    } catch (err) {
      this.showError('Delete failed: ' + (err as Error).message);
    }
  }
}

const app = new App();
app.init().catch(console.error);
